import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useSnackBar } from '@sbv-clients/ui-shared';
import { getApiErrorMessage } from '@sbv-clients/util-misc';

import { useApi } from '../index';
import { ToolsCacheKeys } from './util/cache-keys';

export const useImportSetup = () => {
  const api = useApi();
  return useQuery({
    queryKey: [ToolsCacheKeys.ImportSetup],
    queryFn: () =>
      api.importV2Api.apiV2ToolsImportSetupGet().then((r) => r.data),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useImportPost = () => {
  const api = useApi();
  const { notifyError } = useSnackBar();
  const queryClient = useQueryClient();

  return useMutation(
    (params: {
      file: File;
      effectiveDate?: string;
      addEmployeeIfNotFound?: boolean;
      onlyUpdateEmptyFields?: boolean;
      companyLaborLevelId?: number;
    }) => api.importV2Api.apiV2ToolsImportPost(params).then((r) => r.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ToolsCacheKeys.ImportSetup]);
      },
      onError: (error) => {
        notifyError(getApiErrorMessage(error));
      },
    }
  );
};

export const useImportRunGet = (importRunId: string | null) => {
  const api = useApi();
  return useQuery({
    queryKey: [ToolsCacheKeys.ImportRun, importRunId],
    queryFn: () =>
      api.importV2Api
        .apiV2ToolsImportImportRunIdGet({ importRunId: importRunId! })
        .then((r) => r.data),
    enabled: !!importRunId,
    refetchInterval: (data) => (data?.isActive ? 2000 : false),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useImportRunRowsGet = (
  importRunId: string | null,
  params: { outcome?: string; page?: number; pageSize?: number }
) => {
  const api = useApi();
  return useQuery({
    queryKey: [ToolsCacheKeys.ImportRunRows, importRunId, params],
    queryFn: () =>
      api.importV2Api
        .apiV2ToolsImportImportRunIdRowsGet({
          importRunId: importRunId!,
          ...params,
        })
        .then((r) => r.data),
    enabled: !!importRunId,
    keepPreviousData: true,
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useImportDelete = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifyError } = useSnackBar();

  return useMutation(
    (importRunId: string) =>
      api.importV2Api
        .apiV2ToolsImportImportRunIdDelete({ importRunId })
        .then((r) => r.data),
    {
      onSuccess: (data) => {
        queryClient.setQueryData([ToolsCacheKeys.ImportRun, data.id], data);
      },
      onError: (error) => {
        notifyError(getApiErrorMessage(error));
      },
    }
  );
};

export const useImportUndoPost = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifyError } = useSnackBar();

  return useMutation(
    (importRunId: string) =>
      api.importV2Api
        .apiV2ToolsImportImportRunIdUndoPost({ importRunId })
        .then((r) => r.data),
    {
      onSuccess: (data) => {
        queryClient.setQueryData([ToolsCacheKeys.ImportRun, data.id], data);
      },
      onError: (error) => {
        notifyError(getApiErrorMessage(error));
      },
    }
  );
};

export const useImportLogDownload = () => {
  const api = useApi();
  const { notifyError } = useSnackBar();

  return useMutation(
    async ({
      importRunId,
      fileName,
    }: {
      importRunId: string;
      fileName: string;
    }) => {
      const response = await api.importV2Api.apiV2ToolsImportImportRunIdLogGet(
        { importRunId },
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(
        new Blob([response.data as unknown as BlobPart])
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileName.replace(/\.[^.]+$/, '')}_log.csv`;
      link.click();
      URL.revokeObjectURL(url);
    },
    {
      onError: (error) => {
        notifyError(getApiErrorMessage(error) ?? 'Failed to download log');
      },
    }
  );
};

export const useImportFileDownload = () => {
  const api = useApi();
  const { notifyError } = useSnackBar();

  return useMutation(
    async ({
      importRunId,
      fileName,
    }: {
      importRunId: string;
      fileName: string;
    }) => {
      const response = await api.importV2Api.apiV2ToolsImportImportRunIdFileGet(
        { importRunId },
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(
        new Blob([response.data as unknown as BlobPart])
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
    },
    {
      onError: (error) => {
        notifyError(getApiErrorMessage(error) ?? 'Failed to download file');
      },
    }
  );
};

export const useImportHistory = () => {
  const api = useApi();
  return useQuery({
    queryKey: [ToolsCacheKeys.ImportHistory],
    queryFn: () =>
      api.importV2Api.apiV2ToolsImportHistoryGet().then((r) => r.data),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};
